import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Send,
  Sparkles,
  Clock,
  ChevronRight,
  Database,
  CheckCircle2,
  Loader2,
  FileBarChart,
  Route as RouteIcon,
  LayoutDashboard,
  CornerDownLeft,
  MessageSquarePlus,
  X,
} from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button, Card, StatusBadge, GeoAccent } from "@/components/ui-ext/primitives";
import {
  ConsultantRecommendationResponse,
  CosmosConversation,
  CosmosRecentQuery,
  CosmosQueryResponse,
  RuntimeHealthStatus,
  useCosmosClient,
} from "@/lib/cosmos-api";
import { buildRecommendCommandPrompt, pickRandomSuggestedPrompts } from "@/lib/suggested-prompts";
import { formatConsultantRecommendationMessage, resolveRecommendMessageContent } from "@/lib/consultant-recommendation-message";
import {
  buildClientConversationHistory,
  type ClientConversationExecutionSnapshot,
} from "@/lib/conversation-context";
import {
  WORKSPACE_COMMANDS,
  parseWorkspaceCommand,
  resolveWorkspacePromptRoute,
  resolveWorkspacePromptRouteWithMode,
  stripWorkspaceCommand,
  type ConversationMode,
} from "@/lib/workspace-commands";
import {
  inferConversationMode,
  restoreConversationMessages,
} from "@/lib/restore-conversation-messages";
import {
  formatBadgeValue,
  resolveRowDescription,
  resolveRowDurationMinutes,
  resolveRowStringField,
  resolveRowTitle,
  truncateText,
  visibleColumnsForRows,
} from "@/lib/format";
import { AssistantMarkdown } from "@/components/chat/AssistantMarkdown";
import { ChatMessageFeedback } from "@/components/chat/ChatMessageFeedback";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { cn } from "@/lib/utils";
import {
  buildCanvasViewFromMessage,
  buildDataTypeCounts,
  buildNumericChartData,
  buildSourceTypeChartData,
  resolveRowDataType,
  resolveSourceDataType,
  rowDocumentToJson,
  sourceDocumentToJson,
  canvasRowsForView,
  cardDescription,
  cardTitle,
  cardUrl,
  defaultCanvasTabForView,
  formatModelRoutingLabel,
  formatStreamSourceLabel,
  groupMeilisearchSources,
  isMeilisearchCanvasAnswer,
  messageHasCanvasData,
  metadataBadges,
  sourceSystemLabel,
  summarizeCanvasRows,
  topCanvasCards,
  type WorkspaceCanvasView,
} from "@/lib/workspace-canvas";

type Phase = "empty" | "thinking" | "result";

const CONVERSATION_STORAGE_KEY = "cosmos.activeConversationId";

type ConnectedDocumentType = {
  id: string;
  label: string;
  source: string;
  description: string;
  fields: string[];
};

const CONNECTED_DOCUMENT_TYPES: ConnectedDocumentType[] = [
  {
    id: "events",
    label: "Events",
    source: "LMS",
    description: "Live events and webinars, including schedules, recordings, ratings, reservations, and content blocks.",
    fields: [
      "documentId",
      "type",
      "id",
      "title",
      "description",
      "slug",
      "url",
      "status",
      "visibility",
      "duration",
      "scheduledAt",
      "publishedAt",
      "event",
      "categories",
      "contents",
      "recording",
      "ratings",
      "progressStats",
      "reservationStats",
      "videoTranscripts",
      "searchText",
    ],
  },
  {
    id: "journeys",
    label: "Journeys",
    source: "LMS",
    description: "Certificate programs and learning journeys with hierarchy, metadata, content blocks, and transcript text.",
    fields: [
      "documentId",
      "rootId",
      "id",
      "title",
      "description",
      "type",
      "url",
      "thumbnailUrl",
      "slug",
      "sku",
      "status",
      "difficulty",
      "visibility",
      "isStandAlone",
      "duration",
      "contents",
      "contentTypes",
      "breadcrumb",
      "videoTranscripts",
      "searchText",
      "updatedAt",
      "publishedAt",
    ],
  },
  {
    id: "lessons",
    label: "Lessons",
    source: "LMS",
    description: "Lesson-level learning content, text blocks, embedded files, videos, forms, and transcript availability.",
    fields: [
      "documentId",
      "rootId",
      "parent",
      "id",
      "title",
      "description",
      "type",
      "url",
      "thumbnailUrl",
      "slug",
      "sku",
      "status",
      "difficulty",
      "duration",
      "contents",
      "contentTypes",
      "transcriptAvailable",
      "videoTranscripts",
      "breadcrumb",
      "searchText",
      "updatedAt",
    ],
  },
  {
    id: "modules",
    label: "Modules",
    source: "LMS",
    description: "Module containers that connect journeys to lessons and preserve ordering, parentage, and searchable content.",
    fields: [
      "documentId",
      "rootId",
      "parent",
      "id",
      "title",
      "description",
      "type",
      "url",
      "thumbnailUrl",
      "slug",
      "sku",
      "status",
      "difficulty",
      "visibility",
      "duration",
      "contents",
      "contentTypes",
      "breadcrumb",
      "searchText",
      "updatedAt",
    ],
  },
  {
    id: "courses",
    label: "Courses",
    source: "LMS",
    description: "Standalone and journey-backed courses with metadata, hierarchy, content, and learning duration.",
    fields: [
      "documentId",
      "rootId",
      "id",
      "title",
      "description",
      "type",
      "url",
      "thumbnailUrl",
      "slug",
      "sku",
      "status",
      "difficulty",
      "visibility",
      "isStandAlone",
      "duration",
      "contents",
      "contentTypes",
      "breadcrumb",
      "videoTranscripts",
      "searchText",
      "updatedAt",
      "publishedAt",
    ],
  },
  {
    id: "licenses",
    label: "Licenses",
    source: "License reporting",
    description: "License entitlement documents with product, seat, recurrence, organization, role, and accessible journey data.",
    fields: [
      "documentId",
      "type",
      "LicenseId",
      "LicenseStatus",
      "Product",
      "Variation",
      "IsRecurrent",
      "RecurringFrequency",
      "AccessibleJourneys",
      "AccessibleJourneysIds",
      "LicenseMaxSeatsQuantity",
      "LicenseStartDate",
      "LicenseExpirationDate",
      "MemberCount",
      "ActiveMemberCount",
      "OnboardedMemberCount",
      "Organizations",
      "Roles",
      "Members",
      "searchText",
      "updatedAt",
    ],
  },
  {
    id: "members",
    label: "Members",
    source: "License reporting",
    description: "License-member documents grouped per user, including identity, onboarding, activity, roles, and memberships.",
    fields: [
      "documentId",
      "type",
      "UserId",
      "Email",
      "FirstName",
      "LastName",
      "CreatedAt",
      "UpdatedAt",
      "JoinDate",
      "LastLoginDateTime",
      "IsOnboarded",
      "EnablementStatus",
      "LicenseId",
      "LicenseExpirationDate",
      "Product",
      "Variation",
      "IsUserActive",
      "Roles",
      "Organization",
      "LicenseIds",
      "LicenseMemberships",
      "searchText",
    ],
  },
  {
    id: "blog-posts",
    label: "Blog Posts",
    source: "Commercial website",
    description: "Commercial website articles with cleaned content, taxonomy, SEO metadata, images, and canonical URLs.",
    fields: [
      "documentId",
      "type",
      "sourceId",
      "sourceName",
      "sourceType",
      "id",
      "slug",
      "title",
      "excerpt",
      "content",
      "url",
      "imageUrl",
      "imageAlt",
      "categories",
      "categorySlugs",
      "tags",
      "tagSlugs",
      "seoDescription",
      "searchText",
      "publishedAt",
      "modifiedAt",
      "updatedAt",
    ],
  },
  {
    id: "resources",
    label: "Resources",
    source: "Resource Library",
    description: "Resource-library entries and downloadable assets, including extracted file text when available.",
    fields: [
      "documentId",
      "type",
      "sourceId",
      "sourceName",
      "sourceType",
      "id",
      "slug",
      "title",
      "excerpt",
      "content",
      "url",
      "categories",
      "tags",
      "resourceType",
      "resourceDownloadType",
      "resourceDownloadUrl",
      "resourceFileText",
      "resourceFileTextAvailable",
      "searchText",
      "publishedAt",
      "modifiedAt",
      "updatedAt",
    ],
  },
];

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  prompt?: string;
  status?: "thinking" | "complete" | "error";
  command?: string;
  serverMessageId?: string;
  feedback?: "helpful" | "not_helpful";
  queryResult?: CosmosQueryResponse | null;
  queryError?: string | null;
  executionSnapshot?: ClientConversationExecutionSnapshot;
  consultantResult?: ConsultantRecommendationResponse | null;
  consultantError?: string | null;
};

export function WorkspacePage() {
  const cosmos = useCosmosClient();
  const [phase, setPhase] = useState<Phase>("empty");
  const [prompt, setPrompt] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [conversationMode, setConversationMode] = useState<ConversationMode>("query");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [queryResult, setQueryResult] = useState<CosmosQueryResponse | null>(null);
  const [consultantResult, setConsultantResult] = useState<ConsultantRecommendationResponse | null>(null);
  const [consultantError, setConsultantError] = useState<string | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [tab, setTab] = useState<"summary" | "table" | "chart" | "query" | "sources">("summary");
  const [leftWidth, setLeftWidth] = useState(420);
  const [dragging, setDragging] = useState(false);
  const [recentQueries, setRecentQueries] = useState<CosmosRecentQuery[]>([]);
  const [conversations, setConversations] = useState<CosmosConversation[]>([]);
  const [, setRuntimeHealth] = useState<RuntimeHealthStatus | null>(null);
  const [, setStreamSteps] = useState<string[]>([]);
  const [selectedCanvasMessageId, setSelectedCanvasMessageId] = useState<string | undefined>();
  const containerRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const suggestedPrompts = useMemo(() => pickRandomSuggestedPrompts(3), []);

  const scrollChatToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const panel = chatScrollRef.current;
    if (!panel) {
      return;
    }
    panel.scrollTo({ top: panel.scrollHeight, behavior });
  }, []);

  const persistConversationId = useCallback((id: string | undefined) => {
    if (id) {
      window.localStorage.setItem(CONVERSATION_STORAGE_KEY, id);
    } else {
      window.localStorage.removeItem(CONVERSATION_STORAGE_KEY);
    }
  }, []);

  const refreshConversations = useCallback(async () => {
    try {
      setConversations(await cosmos.listConversations({ limit: 20 }));
    } catch {
      setConversations([]);
    }
  }, [cosmos]);

  const refreshRuntimeHealth = useCallback(async () => {
    try {
      setRuntimeHealth(await cosmos.getRuntimeHealth());
    } catch {
      setRuntimeHealth(null);
    }
  }, [cosmos]);

  const applyCanvasFromMessage = useCallback((message: ChatMessage) => {
    const view = buildCanvasViewFromMessage(message);
    if (!view) {
      return;
    }
    setSelectedCanvasMessageId(message.id);
    setActiveQuery(view.query);
    setQueryResult(view.queryResult);
    setQueryError(view.queryError);
    setConsultantResult(view.consultantResult);
    setConsultantError(view.consultantError);
    setTab(defaultCanvasTabForView(view));
    setPhase("result");
  }, []);

  const appendSourceToQueryResult = useCallback(
    (
      current: CosmosQueryResponse | null | undefined,
      source: CosmosQueryResponse["knowledgeContext"][number],
    ): CosmosQueryResponse => {
      const knowledgeContext = [...(current?.knowledgeContext ?? []), source];
      return {
        intentId: current?.intentId ?? "meilisearch-stream",
        semanticQuery: current?.semanticQuery ?? "",
        sql: current?.sql ?? "",
        rows: current?.rows?.length
          ? current.rows
          : knowledgeContext.map((entry) => ({
              sourceId: entry.sourceId,
              sourceType: entry.sourceType,
              title: entry.title,
              confidence: entry.confidence,
              snippet: entry.snippet,
              uri: entry.uri,
              ...(entry.metadata ?? {}),
            })),
        queryPlan: current?.queryPlan ?? { intent: "answer", domains: ["content"] },
        executions: current?.executions ?? [],
        warnings: current?.warnings ?? [],
        reasoningSummary: current?.reasoningSummary ?? "",
        queryExecution: current?.queryExecution,
        modelRouting: current?.modelRouting ?? {
          provider: "meilisearch",
          model: "meilisearch-chat",
          rationale: "Streaming retrieved sources into the canvas.",
        },
        knowledgeContext,
        assistantResponse: current?.assistantResponse,
        answerProvenance: current?.answerProvenance,
        assistantReasoning: current?.assistantReasoning,
        presentationPlan: current?.presentationPlan,
        chatMode: current?.chatMode,
        runtimeHealth: current?.runtimeHealth,
        conversationId: current?.conversationId,
        executionSnapshot: current?.executionSnapshot,
      };
    },
    [],
  );

  const canvasView = useMemo((): WorkspaceCanvasView => {
    const selected = selectedCanvasMessageId
      ? messages.find((message) => message.id === selectedCanvasMessageId)
      : undefined;
    const fromMessage = selected ? buildCanvasViewFromMessage(selected) : null;
    if (fromMessage) {
      return fromMessage;
    }
    return {
      query: activeQuery,
      queryResult,
      queryError,
      consultantResult,
      consultantError,
      answerText:
        [...messages].reverse().find((message) => message.role === "assistant")?.content ?? "",
      isHistoricalFallback: false,
    };
  }, [
    activeQuery,
    consultantError,
    consultantResult,
    messages,
    queryError,
    queryResult,
    selectedCanvasMessageId,
  ]);

  const selectedAssistantMessage = useMemo(() => {
    const selected = selectedCanvasMessageId
      ? messages.find((message) => message.id === selectedCanvasMessageId)
      : [...messages].reverse().find((message) => message.role === "assistant");
    return selected?.role === "assistant" ? selected : undefined;
  }, [messages, selectedCanvasMessageId]);

  const refreshRecentQueries = useCallback(async () => {
    try {
      setRecentQueries(await cosmos.listRecentQueries(10));
    } catch {
      setRecentQueries([]);
    }
  }, [cosmos]);

  const send = async (text?: string) => {
    const rawPrompt = (text ?? prompt).trim();
    if (!rawPrompt) return;
    const promptRoute = resolveWorkspacePromptRouteWithMode(rawPrompt, conversationMode);
    const parsedCommand = promptRoute.command;
    const q = promptRoute.prompt;
    const userMessage: ChatMessage = {
      id: createMessageId(),
      role: "user",
      content: rawPrompt,
      command: parsedCommand?.cmd,
    };
    const assistantMessageId = createMessageId();
    const updateAssistantMessage = (patch: Partial<ChatMessage>) => {
      setMessages((current) =>
        current.map((message) =>
          message.id === assistantMessageId ? { ...message, ...patch } : message,
        ),
      );
    };

    setMessages((current) => [
      ...current,
      userMessage,
      {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        prompt: q || rawPrompt,
        command: promptRoute.kind === "recommend" ? "/recommend" : parsedCommand?.cmd,
        status: "thinking",
      },
    ]);
    setActiveQuery(q || rawPrompt);
    setPrompt("");
    setQueryError(null);
    setConsultantError(null);
    setQueryResult(null);
    setConsultantResult(null);
    setStreamSteps([]);
    setSelectedCanvasMessageId(assistantMessageId);
    setPhase("thinking");

    if (promptRoute.kind === "recommend") {
      setConversationMode("recommend");
      if (q.length < 3) {
        const message = "Add a short goal after /recommend so Cosmos can find relevant content.";
        setConsultantError(message);
        updateAssistantMessage({
          content: message,
          status: "error",
          consultantError: message,
        });
        setPhase("result");
        return;
      }

      try {
        await cosmos.streamRecommendContent(
          q,
          10,
          {
            onMeta: (meta) => {
              setStreamSteps(
                meta.recommendationEngine === "meilisearch-chat"
                  ? ["Using Meilisearch Chat"]
                  : ["Building study plan"],
              );
              updateAssistantMessage({
                status: "thinking",
                prompt: q || rawPrompt,
                command: "/recommend",
              });
            },
            onStep: (step) => {
              setStreamSteps((current) => [...current, step.label]);
            },
            onDelta: (chunk) => {
              setMessages((current) =>
                current.map((message) =>
                  message.id === assistantMessageId
                    ? { ...message, content: message.content + chunk, status: "thinking" }
                    : message,
                ),
              );
              setPhase("result");
            },
            onDone: (recommendationResponse) => {
              if (recommendationResponse.conversationId) {
                setConversationId(recommendationResponse.conversationId);
                persistConversationId(recommendationResponse.conversationId);
              }
              setConsultantResult(recommendationResponse);
              setMessages((current) =>
                current.map((message) => {
                  if (message.id !== assistantMessageId) {
                    return message;
                  }
                  return {
                    ...message,
                    content: resolveRecommendMessageContent(
                      message.content,
                      recommendationResponse,
                    ),
                    status: "complete",
                    consultantResult: recommendationResponse,
                    prompt: q || rawPrompt,
                    serverMessageId: recommendationResponse.assistantMessageId,
                  };
                }),
              );
              setSelectedCanvasMessageId(assistantMessageId);
            },
            onError: (error) => {
              setConsultantError(error.message);
              updateAssistantMessage({
                content: "Cosmos could not complete the recommendation command.",
                status: "error",
                consultantError: error.message,
              });
            },
          },
          { conversationId },
        );
      } catch (error) {
        const message = error instanceof Error
          ? error.message
          : "Unknown consultant recommendation error";
        setConsultantError(message);
        updateAssistantMessage({
          content: "Cosmos could not complete the recommendation command.",
          status: "error",
          consultantError: message,
        });
      } finally {
        setPhase("result");
      }
      return;
    }

    const streamOptions: {
      conversationId?: string;
      conversationHistory?: ReturnType<typeof buildClientConversationHistory>;
      dashboardMode?: boolean;
      reportMode?: boolean;
      journeyMode?: boolean;
    } = {
      conversationId: conversationId ?? undefined,
      conversationHistory: buildClientConversationHistory(messages),
    };
    if (promptRoute.kind === "dashboard") {
      streamOptions.dashboardMode = true;
    } else if (promptRoute.kind === "report") {
      streamOptions.reportMode = true;
    } else if (promptRoute.kind === "journey") {
      streamOptions.journeyMode = true;
    }

    if (
      (promptRoute.kind === "report" ||
        promptRoute.kind === "journey" ||
        promptRoute.kind === "dashboard") &&
      q.length < 3
    ) {
      const message = `Add details after ${parsedCommand?.cmd ?? "the command"} so Cosmos can compose a useful answer.`;
      setQueryError(message);
      updateAssistantMessage({
        content: message,
        status: "error",
        queryError: message,
      });
      setPhase("result");
      return;
    }

    await cosmos.streamQuery(
      q,
      {
        onMeta: (meta) => {
          if (meta.conversationId) {
            setConversationId(meta.conversationId);
            persistConversationId(meta.conversationId);
          }
          if (meta.runtimeHealth) {
            setRuntimeHealth(meta.runtimeHealth);
          }
          setQueryResult(meta);
          updateAssistantMessage({
            queryResult: meta,
            prompt: q,
            status: "thinking",
          });
          setPhase("result");
        },
        onStep: (step) => {
          const label =
            typeof step.purpose === "string"
              ? step.purpose
              : typeof step.stepId === "string"
                ? step.stepId
                : "Running query step";
          setStreamSteps((current) => [...current, label]);
        },
        onSource: (source) => {
          setQueryResult((current) => appendSourceToQueryResult(current, source));
          updateAssistantMessage((message) => ({
            queryResult: appendSourceToQueryResult(message.queryResult, source),
          }));
        },
        onDelta: (chunk) => {
          setMessages((current) =>
            current.map((message) =>
              message.id === assistantMessageId
                ? { ...message, content: message.content + chunk, status: "thinking" }
                : message,
            ),
          );
          setPhase("result");
        },
        onDone: (response) => {
          if (response.conversationId) {
            setConversationId(response.conversationId);
            persistConversationId(response.conversationId);
          }
          if (response.runtimeHealth) {
            setRuntimeHealth(response.runtimeHealth);
          }
          setQueryResult(response);
          updateAssistantMessage({
            content: response.assistantResponse?.text ?? "",
            queryResult: response,
            executionSnapshot: response.executionSnapshot,
            serverMessageId: response.assistantMessageId,
            status: "complete",
            prompt: q,
          });
          setSelectedCanvasMessageId(assistantMessageId);
          setTab(defaultCanvasTabForView({
            query: q,
            queryResult: response,
            queryError: null,
            consultantResult: null,
            consultantError: null,
            answerText: response.assistantResponse?.text ?? "",
            isHistoricalFallback: false,
          }));
          setPhase("result");
          void refreshRecentQueries();
          void refreshConversations();
        },
        onError: (error) => {
          setQueryError(error.message);
          updateAssistantMessage({
            content: "Cosmos could not complete this query.",
            queryError: error.message,
            status: "error",
          });
          setPhase("result");
        },
      },
      streamOptions,
    ).catch((error) => {
      const message = error instanceof Error ? error.message : "Unknown request error";
      setQueryError(message);
      updateAssistantMessage({
        content: "Cosmos could not complete this query.",
        queryError: message,
        status: "error",
      });
      setPhase("result");
    });
  };

  const reset = () => {
    setPhase("empty");
    setActiveQuery("");
    setConversationId(undefined);
    setConversationMode("query");
    persistConversationId(undefined);
    setMessages([]);
    setStreamSteps([]);
    setQueryResult(null);
    setConsultantResult(null);
    setQueryError(null);
    setConsultantError(null);
    setSelectedCanvasMessageId(undefined);
  };

  const resumeConversation = useCallback(
    async (thread: CosmosConversation) => {
      setConversationId(thread.id);
      persistConversationId(thread.id);
      setPhase("result");
      setQueryError(null);
      setConsultantError(null);
      setQueryResult(null);
      setConsultantResult(null);
      setSelectedCanvasMessageId(undefined);
      try {
        const history = await cosmos.getConversationMessages(thread.id, { limit: 200 });
        const mode = inferConversationMode(thread, history);
        const restoredMessages = restoreConversationMessages(history, mode);
        setConversationMode(mode);
        setMessages(restoredMessages);
        const lastAssistant = [...restoredMessages]
          .reverse()
          .find((entry) => entry.role === "assistant");
        if (lastAssistant) {
          applyCanvasFromMessage(lastAssistant);
        }
        setActiveQuery(
          lastAssistant?.prompt ??
            (stripWorkspaceCommand(
              history.find((entry) => entry.role === "user")?.content ?? thread.title,
            ) || thread.title),
        );
      } catch (error) {
        setQueryError(
          error instanceof Error ? error.message : "Failed to load conversation history.",
        );
      }
    },
    [applyCanvasFromMessage, cosmos, persistConversationId],
  );

  const submitMessageFeedback = async (
    message: ChatMessage,
    verdict: "helpful" | "not_helpful",
    notes?: string,
  ) => {
    if (!conversationId || !message.serverMessageId) {
      throw new Error("Feedback is not available for this message yet.");
    }
    const sourceTitles =
      message.queryResult?.knowledgeContext
        ?.map((source) => source.title)
        .filter((title): title is string => Boolean(title))
        .slice(0, 8) ??
      message.consultantResult?.sourceDocuments
        ?.map((source) => source.title)
        .filter((title): title is string => Boolean(title))
        .slice(0, 8) ??
      message.consultantResult?.ranked
        ?.map((entry) => entry.candidate.title)
        .filter((title): title is string => Boolean(title))
        .slice(0, 8);
    await cosmos.submitAnswerFeedback({
      conversationId,
      messageId: message.serverMessageId,
      verdict,
      notes,
      feedbackContext: {
        ...(message.queryResult?.queryExecution
          ? { queryExecution: message.queryResult.queryExecution }
          : {}),
        ...(message.consultantResult?.recommendationId
          ? { recommendationId: message.consultantResult.recommendationId }
          : {}),
        ...(message.consultantResult?.recommendationEngine
          ? { recommendationEngine: message.consultantResult.recommendationEngine }
          : {}),
        ...(sourceTitles && sourceTitles.length > 0 ? { sourceTitles } : {}),
      },
    });
    setMessages((current) =>
      current.map((entry) =>
        entry.id === message.id ? { ...entry, feedback: verdict } : entry,
      ),
    );
  };

  useEffect(() => {
    void refreshRecentQueries();
    void refreshConversations();
    void refreshRuntimeHealth();
  }, [refreshRecentQueries, refreshConversations, refreshRuntimeHealth]);

  useEffect(() => {
    const storedConversationId = window.localStorage.getItem(CONVERSATION_STORAGE_KEY);
    if (!storedConversationId || conversationId) return;
    void (async () => {
      try {
        const threads = await cosmos.listConversations({ limit: 50 });
        const match = threads.find((thread) => thread.id === storedConversationId);
        if (match) {
          await resumeConversation(match);
        }
      } catch {
        persistConversationId(undefined);
      }
    })();
  }, [conversationId, cosmos, persistConversationId, resumeConversation]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left;
      const min = 320;
      const max = Math.max(min + 200, rect.width - 360);
      setLeftWidth(Math.min(max, Math.max(min, x)));
    };
    const onUp = () => setDragging(false);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  useEffect(() => {
    if (phase === "empty" || messages.length === 0) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      scrollChatToBottom();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages, phase, scrollChatToBottom]);

  return (
    <div
      ref={containerRef}
      className="flex flex-col lg:flex-row h-[calc(100vh-3.5rem)]"
    >
      {/* Thread */}
      <section
        className="flex flex-col min-w-0 overflow-hidden border-b lg:border-b-0 lg:border-r border-soft bg-surface w-full lg:w-[var(--cosmos-left,420px)] lg:shrink-0"
        style={{ ["--cosmos-left" as string]: `${leftWidth}px` }}
      >
        <div className="px-5 py-4 border-b border-soft flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[var(--info-soft)] grid place-items-center">
            <Sparkles className="h-3.5 w-3.5 text-[var(--primary)]" />
          </div>
          <div>
            <div className="text-sm font-semibold text-anchor">Cosmos · Workspace</div>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <ConnectedDataDialog />
            </div>
          </div>
          <button
            onClick={reset}
            className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-anchor"
          >
            <MessageSquarePlus className="h-3.5 w-3.5" />
            New thread
          </button>
        </div>

        <div
          ref={chatScrollRef}
          className="flex-1 overflow-auto scroll-smooth px-5 py-5 space-y-5"
        >
          {phase === "empty" && (
            <>
              <div className="relative overflow-hidden rounded-[14px] border border-soft bg-gradient-to-br from-[var(--info-soft)] to-transparent p-5">
                <GeoAccent className="-right-8 -top-8 h-40 w-40 text-[var(--primary)]" />
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)] mb-1">Good morning, Maya</div>
                <h2 className="text-lg font-bold text-anchor">Ask Cosmos about company, course, and usage data.</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Click a suggested scenario to generate a mixed study plan with /recommend.
                </p>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Suggested study plans</div>
                <div className="grid grid-cols-1 gap-2">
                  {suggestedPrompts.map((p) => (
                    <button
                      key={p}
                      onClick={() => send(buildRecommendCommandPrompt(p))}
                      className="text-left px-3 py-2.5 rounded-[10px] border border-soft bg-background hover:border-[var(--primary)]/40 hover:bg-[var(--info-soft)] transition-colors text-sm text-anchor"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Clock className="h-3 w-3" /> Conversations
                </div>
                <div className="space-y-1.5">
                  {conversations.length === 0 && (
                    <div className="px-3 py-2 rounded-[10px] border border-soft text-xs text-muted-foreground">
                      No saved conversations yet.
                    </div>
                  )}
                  {conversations.map((thread) => (
                    <button
                      key={thread.id}
                      onClick={() => void resumeConversation(thread)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-[10px] hover:bg-muted text-sm flex items-center gap-2",
                        conversationId === thread.id && "bg-muted",
                      )}
                    >
                      <span className="text-anchor truncate">{thread.title}</span>
                      <span className="ml-auto text-[11px] text-muted-foreground shrink-0">
                        {formatRecentDate(thread.updatedAt)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                  Recent prompts
                </div>
                <div className="space-y-1.5">
                  {recentQueries.length === 0 && (
                    <div className="px-3 py-2 rounded-[10px] border border-soft text-xs text-muted-foreground">
                      No recent prompts yet.
                    </div>
                  )}
                  {recentQueries.map((query) => (
                    <button
                      key={`${query.createdAt}-${query.prompt}`}
                      onClick={() => send(query.prompt)}
                      className="w-full text-left px-3 py-2 rounded-[10px] hover:bg-muted text-sm flex items-center"
                    >
                      <span className="text-anchor truncate">{query.prompt}</span>
                      <span className="ml-auto text-[11px] text-muted-foreground">
                        {formatRecentDate(query.createdAt)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {phase !== "empty" && messages.map((message) => (
            <Bubble
              key={message.id}
              role={message.role}
              message={message}
              isCanvasSelected={selectedCanvasMessageId === message.id}
              onSelectCanvas={
                message.role === "assistant" && messageHasCanvasData(message)
                  ? () => applyCanvasFromMessage(message)
                  : undefined
              }
            >
              {message.role === "user" ? (
                <UserMessageContent message={message} />
              ) : (
                <AssistantMessageContent
                  message={message}
                  onSubmitFeedback={(verdict, notes) =>
                    submitMessageFeedback(message, verdict, notes)
                  }
                />
              )}
            </Bubble>
          ))}
        </div>

        <PromptBar
          prompt={prompt}
          setPrompt={setPrompt}
          onSend={send}
        />
      </section>

      {/* Resize handle */}
      <div
        role="separator"
        aria-orientation="vertical"
        onMouseDown={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDoubleClick={() => setLeftWidth(420)}
        className={cn(
          "hidden lg:flex shrink-0 group relative w-1.5 -mx-0.5 cursor-col-resize items-center justify-center z-10",
        )}
      >
        <span
          className={cn(
            "h-10 w-1 rounded-full transition-colors",
            dragging
              ? "bg-[var(--primary)]"
              : "bg-transparent group-hover:bg-[var(--primary)]/40",
          )}
        />
      </div>

      {/* Canvas */}
      <section className="flex flex-col min-w-0 flex-1 bg-background overflow-hidden">
        {phase !== "result" ? (
          <div className="flex-1 grid place-items-center p-8">
            <div className="text-center max-w-md">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-surface border border-soft grid place-items-center mb-4 relative">
                <Database className="h-6 w-6 text-[var(--primary)]" />
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[var(--success)] border-2 border-background" />
              </div>
              <h2 className="text-lg font-bold text-anchor">Result canvas</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Ask a question and the structured answer — tables, charts, and sources — will appear here.
              </p>
              {phase === "thinking" && (
                <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-soft bg-surface text-xs text-anchor">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--primary)]" /> Composing your answer…
                </div>
              )}
            </div>
          </div>
        ) : (
          <ResultCanvas
            tab={tab}
            setTab={setTab}
            canvas={canvasView}
            conversationId={conversationId}
            assistantMessage={selectedAssistantMessage}
            isRecommendConversation={conversationMode === "recommend"}
            onSubmitFeedback={submitMessageFeedback}
          />
        )}
      </section>
    </div>
  );
}

function formatRecentDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const diffMs = date.getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const minuteMs = 60_000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "always" });

  if (absMs < minuteMs) {
    return "just now";
  }
  if (absMs < hourMs) {
    return formatter.format(Math.round(diffMs / minuteMs), "minute");
  }
  if (absMs < dayMs) {
    return formatter.format(Math.round(diffMs / hourMs), "hour");
  }
  return formatter.format(Math.round(diffMs / dayMs), "day");
}

function ConnectedDataDialog() {
  const [selectedTypeId, setSelectedTypeId] = useState(CONNECTED_DOCUMENT_TYPES[0]?.id ?? "");
  const selectedType =
    CONNECTED_DOCUMENT_TYPES.find((entry) => entry.id === selectedTypeId) ??
    CONNECTED_DOCUMENT_TYPES[0];
  const totalAmountOfDocumentTypes = CONNECTED_DOCUMENT_TYPES.length;

  if (!selectedType) {
    return null;
  }

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full text-[11px] text-muted-foreground transition-colors hover:text-anchor"
          aria-label="View connected Cosmos data sources"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--success)]" />
          {totalAmountOfDocumentTypes} sources connected
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/35" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(1080px,calc(100vw-2rem))] max-h-[calc(100vh-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[18px] border border-soft bg-surface shadow-pop">
          <div className="flex items-start justify-between gap-4 border-b border-soft p-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <StatusBadge tone="success" dot>
                  {totalAmountOfDocumentTypes} sources connected
                </StatusBadge>
              </div>
              <Dialog.Title className="text-xl font-bold text-anchor">
                Connected Cosmos data
              </Dialog.Title>
              <Dialog.Description className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Cosmos maps LMS, license, website, and resource-library records into searchable
                document types. Select a node to inspect the fields available behind the scenes.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-[10px] p-2 text-muted-foreground hover:bg-muted hover:text-anchor"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="grid max-h-[calc(100vh-12rem)] gap-0 overflow-auto lg:grid-cols-[minmax(0,1.35fr)_360px]">
            <div className="border-b border-soft p-5 lg:border-b-0 lg:border-r">
              <div className="relative rounded-[16px] border border-soft bg-background p-4">
                <div className="absolute left-1/2 top-[76px] bottom-8 hidden w-px -translate-x-1/2 bg-[var(--border)] md:block" />
                <div className="relative z-10 mx-auto mb-8 w-fit rounded-[14px] border border-[var(--primary)]/25 bg-[var(--info-soft)] px-4 py-3 text-center shadow-card">
                  <div className="mx-auto mb-1 h-8 w-8 rounded-lg bg-surface grid place-items-center">
                    <Sparkles className="h-4 w-4 text-[var(--primary)]" />
                  </div>
                  <div className="text-sm font-semibold text-anchor">
                    Cosmos knowledge index
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Unified search and answer context
                  </div>
                </div>

                <div className="relative z-10 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {CONNECTED_DOCUMENT_TYPES.map((documentType) => {
                    const selected = documentType.id === selectedType.id;
                    return (
                      <button
                        key={documentType.id}
                        type="button"
                        onClick={() => setSelectedTypeId(documentType.id)}
                        className={cn(
                          "rounded-[12px] border bg-surface p-3 text-left transition-colors",
                          selected
                            ? "border-[var(--primary)] bg-[var(--info-soft)]"
                            : "border-soft hover:border-[var(--primary)]/40 hover:bg-[var(--info-soft)]/50",
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className="grid h-8 w-8 place-items-center rounded-lg bg-[var(--success)]/10 text-[var(--success)]">
                            <Database className="h-4 w-4" />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-anchor">
                              {documentType.label}
                            </span>
                            <span className="block truncate text-[11px] text-muted-foreground">
                              {documentType.source}
                            </span>
                          </span>
                        </div>
                        <div className="mt-2 text-[11px] text-muted-foreground line-clamp-2">
                          {documentType.description}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)]">
                    Selected node
                  </div>
                  <h3 className="mt-1 text-lg font-bold text-anchor">{selectedType.label}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{selectedType.description}</p>
                </div>
                <StatusBadge tone="success">{selectedType.source}</StatusBadge>
              </div>

              <div className="mt-5">
                <div className="mb-2 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                  Mapped fields
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedType.fields.map((field) => (
                    <span
                      key={field}
                      className="rounded-full border border-soft bg-background px-2 py-1 text-[11px] font-medium text-anchor"
                    >
                      {field}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-5 rounded-[12px] border border-soft bg-background p-3 text-xs text-muted-foreground">
                These fields are folded into Cosmos retrieval and answer grounding, including
                structured filters where supported and full-text matching through `searchText`.
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Bubble({
  role,
  children,
  message,
  isCanvasSelected,
  onSelectCanvas,
}: {
  role: "user" | "assistant";
  children: React.ReactNode;
  message?: ChatMessage;
  isCanvasSelected?: boolean;
  onSelectCanvas?: () => void;
}) {
  if (role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-[14px] rounded-tr-sm bg-[var(--primary)] text-[color:var(--primary-foreground)] px-3.5 py-2 text-sm">
          {children}
        </div>
      </div>
    );
  }
  const canSelectCanvas = Boolean(onSelectCanvas && message && messageHasCanvasData(message));
  return (
    <div className="flex gap-2.5">
      {canSelectCanvas ? (
        <button
          type="button"
          onClick={onSelectCanvas}
          aria-label="Show this response in results canvas"
          aria-pressed={isCanvasSelected}
          className={cn(
            "h-7 w-7 shrink-0 rounded-md bg-[var(--info-soft)] grid place-items-center transition-colors",
            "hover:bg-[var(--primary)]/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]",
            isCanvasSelected && "ring-2 ring-[var(--primary)] ring-offset-1 ring-offset-background",
          )}
        >
          <Sparkles className="h-3.5 w-3.5 text-[var(--primary)]" />
        </button>
      ) : (
        <div className="h-7 w-7 shrink-0 rounded-md bg-[var(--info-soft)] grid place-items-center">
          <Sparkles className="h-3.5 w-3.5 text-[var(--primary)]" />
        </div>
      )}
      <div className="text-sm text-anchor leading-relaxed pt-0.5">{children}</div>
    </div>
  );
}

function UserMessageContent({ message }: { message: ChatMessage }) {
  return (
    <div className="space-y-1.5">
      {message.command && <CommandBadge command={message.command} />}
      <div>{stripWorkspaceCommand(message.content)}</div>
    </div>
  );
}

function AssistantMessageContent({
  message,
  onSubmitFeedback,
}: {
  message: ChatMessage;
  onSubmitFeedback?: (
    verdict: "helpful" | "not_helpful",
    notes?: string,
  ) => Promise<void>;
}) {
  const queryError = message.queryError;
  const consultantError = message.consultantError;
  const queryResult = message.queryResult;
  const consultantResult = message.consultantResult;
  const canCollectFeedback =
    !queryError &&
    !consultantError &&
    message.status === "complete" &&
    Boolean(message.content.trim()) &&
    Boolean(onSubmitFeedback);

  return (
    <>
      {queryError || consultantError ? (
        <>
          <AssistantMarkdown
            text={message.content || "Cosmos could not complete this request."}
            variant={consultantResult || message.command === "/recommend" ? "recommend" : "default"}
          />
          <div className="mt-2 text-xs text-[var(--danger)]">
            {queryError ?? consultantError}
          </div>
        </>
      ) : (
        <div>
          {message.content ? (
            <AssistantMarkdown
              text={message.content}
              variant={consultantResult || message.command === "/recommend" ? "recommend" : "default"}
            />
          ) : message.status === "thinking" ? (
            <ThinkingSteps />
          ) : queryResult?.rows?.length ? (
            "Query executed successfully. Review structured results in the canvas."
          ) : consultantResult ? (
            "Recommendations are ready in the canvas."
          ) : (
            "No rows returned for this query. Refine filters or time range."
          )}
          {message.status === "thinking" && message.content && (
            <span className="ml-0.5 animate-pulse">▋</span>
          )}
        </div>
      )}
      {!queryError && queryResult?.assistantReasoning && (
        <div className="mt-3 rounded-[10px] border border-soft bg-background/80 p-3">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
            How I reached this answer
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {queryResult.assistantReasoning}
          </p>
        </div>
      )}
      {!queryError && queryResult?.semanticQuery === "content-discovery" && (
        <CourseDiscoveryCards queryResult={queryResult} />
      )}
      {canCollectFeedback && (
        <ChatMessageFeedback
          content={message.content}
          feedback={message.feedback}
          disabled={!message.serverMessageId}
          onSubmit={async (verdict, notes) => {
            if (!onSubmitFeedback) {
              return;
            }
            await onSubmitFeedback(verdict, notes);
          }}
        />
      )}
    </>
  );
}

function CommandBadge({ command }: { command: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-white/15 px-1.5 py-0.5 text-[11px] font-mono">
      {command}
    </span>
  );
}

function CourseDiscoveryCards({ queryResult }: { queryResult: CosmosQueryResponse }) {
  const courses = queryResult.rows
    .filter((row) => resolveRowStringField(row, ["title", "name", "label"]) !== undefined)
    .slice(0, 5);

  if (courses.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 grid gap-2">
      {courses.map((course, index) => {
        const title = resolveRowTitle(course);
        const rawDescription = resolveRowDescription(course);
        const description = rawDescription ? truncateText(rawDescription) : undefined;
        const url = typeof course.url === "string" ? course.url : undefined;
        const rating = typeof course.ratingAverage === "number"
          ? `${course.ratingAverage.toFixed(1)}/5`
          : undefined;
        const durationMinutes = resolveRowDurationMinutes(course);
        const duration = durationMinutes !== undefined ? `${durationMinutes} min` : undefined;
        return (
          <a
            key={`${course.id ?? title}-${index}`}
            href={url}
            target="_blank"
            rel="noreferrer"
            className={cn(
              "block rounded-[12px] border border-soft bg-surface px-3 py-2.5 transition-colors",
              url ? "hover:border-[var(--primary)]/50 hover:bg-[var(--info-soft)]" : "pointer-events-none",
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-anchor truncate">
                  {index + 1}. {title}
                </div>
                {description && (
                  <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                    {description}
                  </div>
                )}
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {course.difficulty && (
                <StatusBadge tone="info">
                  {formatBadgeValue(course, "difficulty", queryResult.queryPlan)}
                </StatusBadge>
              )}
              {duration && <StatusBadge tone="ai">{duration}</StatusBadge>}
              {rating && <StatusBadge tone="success">{rating}</StatusBadge>}
            </div>
          </a>
        );
      })}
    </div>
  );
}

function ThinkingSteps() {
  const steps = ["Parsing your question", "Querying warehouse", "Composing answer"];
  return (
    <div className="flex gap-2.5">
      <div className="h-7 w-7 shrink-0 rounded-md bg-[var(--info-soft)] grid place-items-center">
        <Sparkles className="h-3.5 w-3.5 text-[var(--primary)] animate-pulse" />
      </div>
      <div className="space-y-1.5 pt-1">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2 text-xs text-muted-foreground">
            {i === steps.length - 1 ? (
              <Loader2 className="h-3 w-3 animate-spin text-[var(--primary)]" />
            ) : (
              <CheckCircle2 className="h-3 w-3 text-[var(--success)]" />
            )}
            {s}
          </div>
        ))}
      </div>
    </div>
  );
}

const tabs = [
  { id: "summary", label: "Summary" },
  { id: "table", label: "Table" },
  { id: "chart", label: "Chart" },
  { id: "query", label: "Query" },
  { id: "sources", label: "Sources" },
] as const;

function ResultCanvas({
  tab,
  setTab,
  canvas,
  conversationId,
  assistantMessage,
  isRecommendConversation = false,
  onSubmitFeedback,
}: {
  tab: (typeof tabs)[number]["id"];
  setTab: (t: (typeof tabs)[number]["id"]) => void;
  canvas: WorkspaceCanvasView;
  conversationId?: string;
  assistantMessage?: ChatMessage;
  isRecommendConversation?: boolean;
  onSubmitFeedback?: (
    message: ChatMessage,
    verdict: "helpful" | "not_helpful",
    notes?: string,
  ) => Promise<void>;
}) {
  const [feedbackMessage, setFeedbackMessage] = useState<ChatMessage["feedback"]>(
    assistantMessage?.feedback,
  );
  const {
    query,
    queryResult,
    queryError,
    consultantResult,
    consultantError,
    answerText,
    isHistoricalFallback,
    executionSnapshot,
  } = canvas;
  const canvasRows = canvasRowsForView(canvas);
  const isMeilisearchAnswer = isMeilisearchCanvasAnswer(queryResult);
  const meilisearchGroups = groupMeilisearchSources(queryResult?.knowledgeContext);
  const rowSummary = summarizeCanvasRows(canvasRows);
  const dataTypeCounts = buildDataTypeCounts(canvasRows);
  const chartData =
    dataTypeCounts.length > 0
      ? buildSourceTypeChartData(canvasRows)
      : buildNumericChartData(canvasRows);
  const isMeilisearchSourceTable = canvasRows.some((row) => row.sourceType === "meilisearch");
  const meilisearchSearchCount = new Set(
    canvasRows
      .map((row) =>
        typeof row.searchCallId === "string"
          ? row.searchCallId
          : typeof row.searchQuery === "string"
            ? row.searchQuery
            : undefined,
      )
      .filter((value): value is string => Boolean(value)),
  ).size;
  const consultantAnswerText =
    answerText.trim() ||
    (consultantResult ? formatConsultantRecommendationMessage(consultantResult) : "");
  const isRecommendFlow =
    isRecommendConversation ||
    Boolean(consultantResult) ||
    assistantMessage?.command === "/recommend" ||
    Boolean(assistantMessage?.consultantResult);
  const isRecommendStreaming =
    isRecommendFlow &&
    !consultantResult &&
    assistantMessage?.status === "thinking";
  const activeConsultantResult = consultantResult ?? assistantMessage?.consultantResult ?? null;
  const canSubmitCanvasFeedback = Boolean(
    assistantMessage &&
      onSubmitFeedback &&
      conversationId &&
      assistantMessage.serverMessageId &&
      consultantAnswerText,
  );

  useEffect(() => {
    setFeedbackMessage(assistantMessage?.feedback);
  }, [assistantMessage?.id, assistantMessage?.feedback]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-6 pt-5 pb-3 border-b border-soft bg-surface">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)]">Result</div>
          <h2 className="text-lg font-bold text-anchor truncate">{query}</h2>
          {(queryError || queryResult || isRecommendFlow) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {queryError && (
                <StatusBadge tone="danger" dot>
                  Request failed
                </StatusBadge>
              )}
              {queryResult?.modelRouting?.provider && (
                <StatusBadge tone="info" dot>
                  {formatModelRoutingLabel(
                    queryResult.modelRouting.provider,
                    queryResult.modelRouting.model,
                  )}
                </StatusBadge>
              )}
              {queryResult?.queryExecution?.streamSource && (
                <StatusBadge tone="ai" dot>
                  {formatStreamSourceLabel(queryResult.queryExecution.streamSource)}
                </StatusBadge>
              )}
              {queryResult && (
                <StatusBadge tone="ai">
                  {(queryResult.knowledgeContext?.length ?? 0)} sources
                </StatusBadge>
              )}
            </div>
          )}
        </div>
        <div className="mt-4 flex items-center gap-1 -mb-3 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                tab === t.id ? "border-[var(--primary)] text-anchor" : "border-transparent text-muted-foreground hover:text-anchor",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {tab === "summary" && (
          <div className="grid gap-4">
            {isHistoricalFallback && (
              <Card className="p-4 border-soft bg-surface">
                <div className="text-xs text-muted-foreground">
                  This conversation was loaded from history. Full structured rows and source
                  documents are only available for responses in the current session.
                </div>
              </Card>
            )}
            {!isRecommendFlow && (answerText || queryError || consultantError) && (
              <Card className="p-5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)]">
                  Answer
                </div>
                {queryError || consultantError ? (
                  <p className="mt-2 text-sm text-[var(--danger)]">
                    {queryError ?? consultantError}
                  </p>
                ) : (
                  <div className="prose prose-sm max-w-none mt-2 text-anchor">
                    <AssistantMarkdown text={answerText} />
                  </div>
                )}
              </Card>
            )}
            {canvasRows.length > 0 && (
              <ResultOverview
                rows={canvasRows}
                entityCounts={rowSummary.entityCounts}
                averageRating={rowSummary.averageRating}
                linkedSources={rowSummary.linkedSources}
              />
            )}
            {isRecommendFlow && (
            <Card className="p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)]">
                    Consultant mode
                  </div>
                  <h3 className="text-sm font-semibold text-anchor">
                    {activeConsultantResult?.recommendationEngine === "meilisearch-chat"
                      ? "Meilisearch study plan"
                      : "Content recommendations"}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {activeConsultantResult?.recommendationEngine === "meilisearch-chat"
                      ? "Grounded study plan composed by Meilisearch Chat over indexed learning content."
                      : "Mixed study plan across journeys, courses, lessons, modules, events, blog posts, and resources."}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {activeConsultantResult?.recommendationId && (
                    <StatusBadge tone="ai" dot>
                      {activeConsultantResult.recommendationId.slice(0, 8)}
                    </StatusBadge>
                  )}
                </div>
              </div>

              {consultantError && (
                <div className="mt-3 rounded-[10px] border border-[var(--danger)]/30 bg-[var(--danger)]/5 px-3 py-2 text-xs text-[var(--danger)]">
                  {consultantError}
                </div>
              )}

              {(activeConsultantResult || isRecommendStreaming) && (
                <div className="mt-4 rounded-[10px] border border-soft bg-background/80 p-4">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                    Study plan
                  </div>
                  {activeConsultantResult ? (
                    <StudyPlanTree result={activeConsultantResult} />
                  ) : (
                    <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--primary)]" />
                      Composing study plan…
                    </div>
                  )}
                </div>
              )}

              {activeConsultantResult && (
                <>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                    <MiniCandidateList
                      title="Journeys"
                      items={activeConsultantResult.candidates.journeys.map((item) => item.title)}
                    />
                    <MiniCandidateList
                      title="Courses"
                      items={activeConsultantResult.candidates.courses.map((item) => item.title)}
                    />
                    <MiniCandidateList
                      title="Lessons"
                      items={activeConsultantResult.candidates.lessons.map((item) => item.title)}
                    />
                    <MiniCandidateList
                      title="Modules"
                      items={activeConsultantResult.candidates.modules.map((item) => item.title)}
                    />
                    <MiniCandidateList
                      title="Events"
                      items={activeConsultantResult.candidates.events.map((item) => item.title)}
                    />
                    <MiniCandidateList
                      title="Blog posts"
                      items={activeConsultantResult.candidates.blogPosts.map((item) => item.title)}
                    />
                    <MiniCandidateList
                      title="Resources"
                      items={activeConsultantResult.candidates.resources.map((item) => item.title)}
                    />
                  </div>
                </>
              )}

              {canSubmitCanvasFeedback && assistantMessage && onSubmitFeedback && (
                <ChatMessageFeedback
                  content={consultantAnswerText}
                  feedback={feedbackMessage}
                  disabled={false}
                  onSubmit={async (verdict, notes) => {
                    await onSubmitFeedback(assistantMessage, verdict, notes);
                    setFeedbackMessage(verdict);
                  }}
                />
              )}
            </Card>
            )}
            {queryResult?.presentationPlan?.explanation && (
              <Card className="p-4 border-soft bg-surface">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                  Presentation
                </div>
                <p className="text-sm text-anchor">{queryResult.presentationPlan.explanation}</p>
              </Card>
            )}
            <CanvasResultCardGrid rows={topCanvasCards(canvas)} queryResult={queryResult} />
            {queryResult?.assistantResponse?.mode === "fallback" && (
              <Card className="p-4 border-[var(--primary)]/20 bg-[var(--info-soft)]">
                <div className="text-xs text-muted-foreground">
                  Cosmos answered with deterministic templates because the live planner is not configured.
                  Set <code>COSMOS_MODEL_INVOCATION_MODE=live</code> for richer, narrative answers.
                </div>
              </Card>
            )}
          </div>
        )}

        {tab === "chart" && (
          <div className="grid gap-4">
            <Card className="p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-sm font-semibold text-anchor">Data types</h3>
                <StatusBadge tone="info">{canvasRows.length} documents</StatusBadge>
              </div>
              {dataTypeCounts.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  No documents are available to count by type for this response.
                </p>
              ) : (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {dataTypeCounts.map(({ label, count }) => (
                    <div
                      key={label}
                      className="rounded-[10px] border border-soft bg-[var(--info-soft)]/30 px-3 py-2.5"
                    >
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground truncate">
                        {label}
                      </div>
                      <div className="mt-1 text-2xl font-bold text-anchor tabular-nums">{count}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card className="p-5">
              <h3 className="text-sm font-semibold text-anchor mb-3">Distribution</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid stroke="#e2e5f3" strokeDasharray="3 3" />
                    <XAxis dataKey="label" stroke="#989fbb" fontSize={11} />
                    <YAxis stroke="#989fbb" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 10, borderColor: "#e2e5f3", fontSize: 12 }} />
                    <Bar dataKey="value" fill="#1ebbf0" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        )}

        {tab === "table" && (
          <div className="space-y-3">
            {canvasRows.length === 0 ? (
              <Card className="p-5 text-sm text-muted-foreground">
                No structured rows are available for this response.
              </Card>
            ) : (
              <>
                {isMeilisearchSourceTable && (
                  <Card className="p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <h3 className="text-sm font-semibold text-anchor">
                          Meilisearch source documents
                        </h3>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Documents returned by `_meiliSearchSources` and linked to searches from `_meiliSearchProgress`.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <StatusBadge tone="ai">{canvasRows.length} documents</StatusBadge>
                        {meilisearchSearchCount > 0 && (
                          <StatusBadge tone="info">{meilisearchSearchCount} searches</StatusBadge>
                        )}
                      </div>
                    </div>
                  </Card>
                )}
                {canvasRows.map((row, index) => (
                  <JsonDocumentCard
                    key={`${resolveRowStringField(row, ["sourceId", "id"]) ?? index}`}
                    title={cardTitle(row, index)}
                    subtitle={resolveRowDataType(row)}
                    document={rowDocumentToJson(row)}
                  />
                ))}
              </>
            )}
          </div>
        )}

        {tab === "query" && (
          <div className="space-y-3">
            {isMeilisearchAnswer ? (
              <>
                <CanvasRetrievalMetaCard
                  workspace={queryResult?.queryExecution?.workspace ?? "cosmos"}
                  indexUid={
                    queryResult?.queryExecution?.indexUid ??
                    meilisearchGroups[0]?.index ??
                    "global"
                  }
                  estimatedTotalHits={queryResult?.queryExecution?.estimatedTotalHits}
                  documentCount={queryResult?.knowledgeContext?.length ?? 0}
                />
                {meilisearchGroups.length > 0 ? (
                  meilisearchGroups.map((group) => (
                    <div key={`${group.callId}-${group.query}`} className="space-y-2">
                      <div className="px-1 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                        Search · {group.index ?? "index"} · {group.documents.length} documents
                      </div>
                      <p className="px-1 text-xs text-muted-foreground font-mono">{group.query}</p>
                      {group.documents.map((source, index) => (
                        <JsonDocumentCard
                          key={source.sourceId}
                          title={`${source.title || `Document ${index + 1}`}`}
                          subtitle={resolveSourceDataType(source)}
                          document={sourceDocumentToJson(source)}
                        />
                      ))}
                    </div>
                  ))
                ) : (queryResult?.knowledgeContext?.length ?? 0) > 0 ? (
                  queryResult?.knowledgeContext.map((source, index) => (
                    <JsonDocumentCard
                      key={source.sourceId}
                      title={source.title || `Document ${index + 1}`}
                      subtitle={resolveSourceDataType(source)}
                      document={sourceDocumentToJson(source)}
                    />
                  ))
                ) : (
                  <Card className="p-5 text-sm text-muted-foreground">
                    No source documents were captured for this response.
                  </Card>
                )}
                {(queryResult?.executions?.length ?? 0) > 0 && (
                  <Card className="p-5">
                    <div className="text-xs font-semibold text-anchor mb-2">Retrieval steps</div>
                    <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                      {queryResult?.executions.map((step, index) => {
                        const purpose =
                          typeof step.purpose === "string" ? step.purpose : undefined;
                        const stepId =
                          typeof step.stepId === "string" ? step.stepId : undefined;
                        return (
                          <li key={`${stepId ?? purpose ?? index}`}>
                            {purpose ?? stepId ?? `Step ${index + 1}`}
                          </li>
                        );
                      })}
                    </ol>
                  </Card>
                )}
              </>
            ) : (
              <>
                {queryResult?.sql && (
                  <Card className="p-0 overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-soft text-[11px] uppercase tracking-wider text-muted-foreground">
                      {`Generated SQL${queryResult.queryExecution?.engine ? ` · ${queryResult.queryExecution.engine}` : ""}`}
                    </div>
                    <pre className="p-5 text-[12.5px] leading-relaxed text-anchor overflow-auto bg-[var(--info-soft)]/40">
                      {queryResult.sql}
                    </pre>
                  </Card>
                )}
                {canvasRows.length > 0 ? (
                  canvasRows.map((row, index) => (
                    <JsonDocumentCard
                      key={`${resolveRowStringField(row, ["id", "sourceId"]) ?? index}`}
                      title={cardTitle(row, index)}
                      subtitle={resolveRowDataType(row)}
                      document={rowDocumentToJson(row)}
                    />
                  ))
                ) : (
                  <Card className="p-5 text-sm text-muted-foreground">
                    {queryResult?.sql ? "SQL ran but returned no row documents." : "No query or documents for this response."}
                  </Card>
                )}
              </>
            )}
            {isHistoricalFallback && executionSnapshot && (
              <Card className="p-5">
                <div className="text-xs font-semibold text-anchor mb-2">Saved execution snapshot</div>
                <pre className="mt-2 text-[11px] leading-relaxed text-anchor overflow-auto max-h-64 rounded-[10px] border border-soft bg-[var(--info-soft)]/40 p-3">
                  {JSON.stringify(executionSnapshot, null, 2)}
                </pre>
              </Card>
            )}
          </div>
        )}

        {tab === "sources" && (
          <div className="space-y-3">
            {queryResult?.answerProvenance?.freshnessSummary && (
              <Card className="p-4 text-xs text-muted-foreground">
                <div className="font-semibold text-anchor mb-1">Data freshness</div>
                <div>{queryResult.answerProvenance.freshnessSummary}</div>
                <div className="mt-1">
                  Verification: {queryResult.answerProvenance.verificationStatus}
                </div>
              </Card>
            )}
            {meilisearchGroups.length > 0 ? (
              meilisearchGroups.map((group) => (
                <div key={`${group.callId}-${group.query}`} className="space-y-2">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground px-1">
                    {group.query}
                    {group.index ? ` · ${group.index}` : ""} · {group.documents.length} sources
                  </div>
                  {group.documents.map((source) => (
                    <KnowledgeSourceCard key={source.sourceId} source={source} />
                  ))}
                </div>
              ))
            ) : (queryResult?.knowledgeContext?.length ?? 0) > 0 ? (
              queryResult?.knowledgeContext.map((source) => (
                <KnowledgeSourceCard key={source.sourceId} source={source} />
              ))
            ) : (
              <Card className="p-4 text-sm text-muted-foreground">No sources for this response.</Card>
            )}
            {(queryResult?.answerProvenance?.sources?.length ?? 0) > 0 && (
              <Card className="p-4">
                <div className="text-xs font-semibold text-anchor mb-2">Execution sources</div>
                <div className="space-y-1 text-[11px] text-muted-foreground">
                  {queryResult?.answerProvenance?.sources.map((source) => (
                    <div key={source.sourceId}>
                      {source.displayName ?? source.sourceId}
                      {source.lastRefreshedAt ? ` · refreshed ${source.lastRefreshedAt}` : ""}
                      {source.rowCount !== undefined ? ` · ${source.rowCount} rows` : ""}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

function CanvasRetrievalMetaCard({
  workspace,
  indexUid,
  estimatedTotalHits,
  documentCount,
}: {
  workspace: string;
  indexUid: string;
  estimatedTotalHits?: number;
  documentCount: number;
}) {
  return (
    <Card className="p-5">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--primary)]">
        Search retrieval
      </div>
      <dl className="mt-3 grid gap-2 text-sm">
        <div className="flex gap-2">
          <dt className="text-muted-foreground shrink-0">Workspace</dt>
          <dd className="text-anchor font-medium">{workspace}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-muted-foreground shrink-0">Index</dt>
          <dd className="text-anchor font-medium">{indexUid}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-muted-foreground shrink-0">Documents</dt>
          <dd className="text-anchor font-medium">{documentCount}</dd>
        </div>
        {estimatedTotalHits !== undefined && (
          <div className="flex gap-2">
            <dt className="text-muted-foreground shrink-0">Estimated hits</dt>
            <dd className="text-anchor font-medium">{estimatedTotalHits}</dd>
          </div>
        )}
      </dl>
    </Card>
  );
}

function JsonDocumentCard({
  title,
  subtitle,
  document,
}: {
  title: string;
  subtitle: string;
  document: Record<string, unknown>;
}) {
  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-soft flex items-center justify-between gap-3">
        <div className="min-w-0 text-sm font-medium text-anchor truncate">{title}</div>
        <StatusBadge tone="info">{subtitle}</StatusBadge>
      </div>
      <pre className="p-5 text-[11px] font-mono leading-relaxed text-anchor overflow-auto max-h-[28rem] bg-[var(--info-soft)]/40">
        {JSON.stringify(document, null, 2)}
      </pre>
    </Card>
  );
}

function MiniCandidateList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-[10px] border border-soft p-3">
      <div className="text-xs font-semibold text-anchor">{title}</div>
      <div className="mt-1 text-xs text-muted-foreground space-y-1">
        {items.length === 0 && <div>No matches</div>}
        {items.slice(0, 3).map((item) => (
          <div key={item} className="truncate">
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

const consultantEntityTypeLabel = (entityType: string): string => {
  switch (entityType) {
    case "journey":
      return "Journey";
    case "course":
      return "Course";
    case "lesson":
      return "Lesson";
    case "module":
      return "Module";
    case "event":
      return "Event";
    case "blogPost":
      return "Blog post";
    case "resource":
      return "Resource";
    default:
      return entityType;
  }
};

const groupStudyPlanStepsByPhase = (
  steps: ConsultantRecommendationResponse["journeyBlueprint"]["steps"],
) => {
  const groups: Array<{
    phase: string;
    steps: ConsultantRecommendationResponse["journeyBlueprint"]["steps"];
  }> = [];

  for (const step of steps) {
    const phase = step.phase?.trim() || "Study plan";
    const current = groups[groups.length - 1];
    if (current?.phase === phase) {
      current.steps.push(step);
    } else {
      groups.push({ phase, steps: [step] });
    }
  }

  return groups;
};

const visibleStudyPlanSignals = (signals: string[]): string[] =>
  signals.filter((signal) => signal !== "source:meilisearch-chat");

function StudyPlanTree({ result }: { result: ConsultantRecommendationResponse }) {
  const groups = groupStudyPlanStepsByPhase(result.journeyBlueprint.steps.slice(0, 12));

  const rankedForStep = (
    step: ConsultantRecommendationResponse["journeyBlueprint"]["steps"][number],
  ) =>
    result.ranked.find(
      (entry) =>
        entry.candidate.id === step.entityId ||
        entry.candidate.title.toLowerCase() === step.title.toLowerCase(),
    );

  return (
    <div className="space-y-4">
      <div className="rounded-[10px] border border-soft bg-[var(--info-soft)]/30 p-3">
        <div className="flex items-start gap-2">
          <div className="mt-0.5 h-7 w-7 rounded-full bg-[var(--primary)]/10 grid place-items-center">
            <RouteIcon className="h-3.5 w-3.5 text-[var(--primary)]" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-anchor">
              {result.journeyBlueprint.title}
            </div>
            {result.journeyBlueprint.summary && (
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {result.journeyBlueprint.summary}
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-1.5">
              <StatusBadge tone="info">
                {result.journeyBlueprint.steps.length} nodes
              </StatusBadge>
              {result.journeyBlueprint.totalEstimatedMinutes > 0 && (
                <StatusBadge tone="ai">
                  {result.journeyBlueprint.totalEstimatedMinutes} min
                </StatusBadge>
              )}
            </div>
          </div>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-[10px] border border-dashed border-soft p-4 text-xs text-muted-foreground">
          No structured study-plan nodes were returned for this recommendation.
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group, groupIndex) => (
            <div key={`${group.phase}-${groupIndex}`} className="relative pl-5">
              <div className="absolute left-2 top-6 bottom-0 w-px bg-border" />
              <div className="relative flex items-center gap-2">
                <div className="absolute -left-5 h-4 w-4 rounded-full border border-[var(--primary)] bg-surface" />
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--primary)]">
                  {group.phase}
                </div>
              </div>
              <div className="mt-2 space-y-2.5">
                {group.steps.map((step, stepIndex) => {
                  const ranked = rankedForStep(step);
                  const candidate = ranked?.candidate;
                  const signals = visibleStudyPlanSignals(ranked?.matchedSignals ?? []);
                  const nodeNumber =
                    result.journeyBlueprint.steps.findIndex((entry) => entry === step) + 1;
                  const meta = [
                    consultantEntityTypeLabel(step.entityType),
                    candidate?.difficulty,
                    step.durationMinutes ? `${step.durationMinutes} min` : undefined,
                  ].filter((entry): entry is string => Boolean(entry));

                  return (
                    <div
                      key={`${step.entityId ?? step.title}-${stepIndex}`}
                      className="relative ml-4 rounded-[10px] border border-soft bg-surface p-3"
                    >
                      <div className="absolute -left-[1.55rem] top-5 h-px w-6 bg-border" />
                      <div className="flex items-start gap-3">
                        <div className="h-6 w-6 shrink-0 rounded-full bg-[var(--info-soft)] grid place-items-center text-[11px] font-semibold text-[var(--primary)]">
                          {nodeNumber}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {candidate?.url ? (
                              <a
                                href={candidate.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cosmos-chat-link text-sm font-semibold"
                              >
                                {step.title}
                              </a>
                            ) : (
                              <div className="text-sm font-semibold text-anchor">
                                {step.title}
                              </div>
                            )}
                            {ranked && (
                              <StatusBadge tone="success">
                                Score {ranked.score.toFixed(1)}
                              </StatusBadge>
                            )}
                          </div>
                          {meta.length > 0 && (
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              {meta.join(" · ")}
                            </div>
                          )}
                          {step.why && (
                            <p className="mt-2 text-xs leading-relaxed text-anchor">
                              {step.why}
                            </p>
                          )}
                          {signals.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {signals.slice(0, 3).map((signal) => (
                                <StatusBadge key={signal} tone="info">
                                  {signal}
                                </StatusBadge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultOverview({
  rows,
  entityCounts: entityCountsInput,
  averageRating: averageRatingInput,
  linkedSources,
}: {
  rows: CosmosQueryResponse["rows"];
  entityCounts?: Record<string, number>;
  averageRating?: number;
  linkedSources?: boolean;
}) {
  const entityCounts =
    entityCountsInput ??
    rows.reduce<Record<string, number>>((counts, row) => {
      const entityType =
        typeof row.entityType === "string"
          ? row.entityType
          : typeof row.type === "string"
            ? row.type
            : "result";
      counts[entityType] = (counts[entityType] ?? 0) + 1;
      return counts;
    }, {});
  const ratings = rows
    .map((row) => row.ratingAverage)
    .filter((rating): rating is number => typeof rating === "number");
  const averageRating =
    averageRatingInput ??
    (ratings.length > 0
      ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
      : undefined);
  const hasLinks =
    linkedSources ??
    rows.some((row) => typeof row.url === "string" || typeof row.uri === "string");

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <MetricCard label="Total results" value={String(rows.length)} />
      <MetricCard
        label="Entity types"
        value={String(Object.keys(entityCounts).length)}
        detail={Object.entries(entityCounts)
          .map(([type, count]) => `${type}: ${count}`)
          .join(" · ") || "n/a"}
      />
      <MetricCard
        label="Avg rating"
        value={averageRating ? averageRating.toFixed(1) : "n/a"}
      />
      <MetricCard label="Sources" value={hasLinks ? "Linked" : "n/a"} />
    </div>
  );
}

function KnowledgeSourceCard({
  source,
}: {
  source: CosmosQueryResponse["knowledgeContext"][number];
}) {
  const badges = metadataBadges(source.metadata);
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className="h-9 w-9 rounded-md bg-[var(--info-soft)] grid place-items-center">
        <Database className="h-4 w-4 text-[var(--primary)]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-anchor">{source.title}</div>
        <div className="text-xs text-muted-foreground">{sourceSystemLabel(source.sourceType)}</div>
        {source.snippet && (
          <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{source.snippet}</div>
        )}
        {badges.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {badges.map((badge) => (
              <StatusBadge key={badge.label} tone={badge.tone}>
                {badge.label}
              </StatusBadge>
            ))}
          </div>
        )}
        {source.uri && (
          <a
            href={source.uri}
            target="_blank"
            rel="noopener noreferrer"
            className="cosmos-chat-link text-[11px] mt-1 inline-block"
          >
            Open source
          </a>
        )}
      </div>
      <StatusBadge tone="success" dot>
        Confidence · {Math.round(source.confidence * 100)}%
      </StatusBadge>
    </Card>
  );
}

function CanvasResultCardGrid({
  rows,
  queryResult,
}: {
  rows: CosmosQueryResponse["rows"];
  queryResult?: CosmosQueryResponse | null;
}) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
      {rows.map((row, index) => (
        <CanvasSummaryCard
          key={`${resolveRowStringField(row, ["id", "sourceId"]) ?? cardTitle(row, index)}-${index}`}
          row={row}
          index={index}
          queryResult={queryResult}
        />
      ))}
    </div>
  );
}

function CanvasSummaryCard({
  row,
  index,
  queryResult,
}: {
  row: CosmosQueryResponse["rows"][number];
  index: number;
  queryResult?: CosmosQueryResponse | null;
}) {
  const title = cardTitle(row, index);
  const description = cardDescription(row);
  const url = cardUrl(row);
  const badges = metadataBadges(row);
  const content = (
    <Card className="p-4 h-full">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-anchor truncate">{title}</div>
          {description && (
            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{description}</p>
          )}
        </div>
        {url && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {typeof row.entityType === "string" && (
          <StatusBadge tone="info">{row.entityType}</StatusBadge>
        )}
        {typeof row.type === "string" && <StatusBadge tone="info">{row.type}</StatusBadge>}
        {badges.map((badge) => (
          <StatusBadge key={badge.label} tone={badge.tone}>
            {badge.label}
          </StatusBadge>
        ))}
        {row.difficulty && (
          <StatusBadge tone="ai">
            {formatBadgeValue(row, "difficulty", queryResult?.queryPlan)}
          </StatusBadge>
        )}
        {typeof row.ratingAverage === "number" && (
          <StatusBadge tone="success">{row.ratingAverage.toFixed(1)}/5</StatusBadge>
        )}
      </div>
    </Card>
  );

  if (!url) {
    return content;
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" className="block h-full">
      {content}
    </a>
  );
}

function MetricCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <Card className="p-4">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-anchor">{value}</div>
      {detail && <div className="mt-1 text-xs text-muted-foreground">{detail}</div>}
    </Card>
  );
}

type SlashCmd = {
  cmd: string;
  label: string;
  desc: string;
  icon: typeof FileBarChart;
  tone: string;
  status: "available" | "coming-soon";
};

const commandPresentation: Record<string, Pick<SlashCmd, "icon" | "tone">> = {
  "/recommend": {
    icon: Sparkles,
    tone: "bg-[var(--ai)]/15 text-[var(--ai)]",
  },
  "/report": {
    icon: FileBarChart,
    tone: "bg-[var(--azure)]/15 text-[var(--azure)]",
  },
  "/journey": {
    icon: RouteIcon,
    tone: "bg-[var(--violet)]/15 text-[var(--violet)]",
  },
  "/dashboard": {
    icon: LayoutDashboard,
    tone: "bg-[var(--primary)]/12 text-[var(--primary)]",
  },
};

const SLASH_COMMANDS: SlashCmd[] = WORKSPACE_COMMANDS.map((command) => ({
  ...command,
  ...commandPresentation[command.cmd],
}));

const createMessageId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

function PromptBar({
  prompt,
  setPrompt,
  onSend,
}: {
  prompt: string;
  setPrompt: (v: string) => void;
  onSend: (text?: string) => void;
}) {
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashIdx, setSlashIdx] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const slashQuery = useMemo(() => {
    if (!prompt.startsWith("/")) return null;
    const firstWord = prompt.split(/\s/)[0];
    return firstWord.slice(1).toLowerCase();
  }, [prompt]);

  const filteredCmds = useMemo(() => {
    if (slashQuery === null) return [];
    return SLASH_COMMANDS.filter((c) => c.cmd.slice(1).startsWith(slashQuery));
  }, [slashQuery]);

  useEffect(() => {
    setSlashOpen(slashQuery !== null && filteredCmds.length > 0);
    setSlashIdx(0);
  }, [slashQuery, filteredCmds.length]);

  const applyCommand = (cmd: SlashCmd) => {
    if (cmd.status === "coming-soon") {
      return;
    }
    const rest = prompt.includes(" ") ? prompt.slice(prompt.indexOf(" ")) : "";
    setPrompt(cmd.cmd + (rest || " "));
    setSlashOpen(false);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSlashIdx((i) => Math.min(i + 1, filteredCmds.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSlashIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const c = filteredCmds[slashIdx];
        if (c?.status === "available") applyCommand(c);
        return;
      }
      if (e.key === "Escape") {
        setSlashOpen(false);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const activeCommand = useMemo(() => parseWorkspaceCommand(prompt), [prompt]);

  return (
    <div className="sticky bottom-0 z-30 shrink-0 border-t border-soft p-3 bg-surface">
      <div className="relative">
        {/* Slash command popover */}
        {slashOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-2 z-30 rounded-[12px] border border-soft bg-popover text-popover-foreground shadow-[0_20px_50px_-12px_rgba(23,15,63,0.25),0_4px_16px_-8px_rgba(23,15,63,0.15)] overflow-hidden">
            <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground border-b border-soft">
              Commands
            </div>
            <ul className="py-1">
              {filteredCmds.map((c, i) => {
                const Icon = c.icon;
                const active = i === slashIdx;
                const isComingSoon = c.status === "coming-soon";
                return (
                  <li key={c.cmd}>
                    <button
                      aria-disabled={isComingSoon}
                      onMouseEnter={() => setSlashIdx(i)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (!isComingSoon) {
                          applyCommand(c);
                        }
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-left transition-colors",
                        active ? "bg-muted/70" : "hover:bg-muted/40",
                        isComingSoon && "opacity-75",
                      )}
                    >
                      <div className={cn("h-7 w-7 shrink-0 rounded-md grid place-items-center", c.tone)}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[12.5px] font-medium text-anchor">{c.cmd}</span>
                          <span className="text-[12px] text-muted-foreground truncate">{c.label}</span>
                          {isComingSoon && (
                            <span className="shrink-0 rounded-full border border-[var(--warning)]/20 bg-[var(--warning)]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#9a6500]">
                              SOON
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">{c.desc}</div>
                      </div>
                      {active && !isComingSoon && (
                        <span className="text-muted-foreground shrink-0">
                          <CornerDownLeft className="h-3 w-3" />
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="px-3 py-1.5 border-t border-soft text-[10.5px] text-muted-foreground flex items-center gap-2">
              <Kbd>↑</Kbd><Kbd>↓</Kbd> navigate
              <Kbd>↵</Kbd> select
              <Kbd>esc</Kbd> dismiss
            </div>
          </div>
        )}

        <div className="rounded-[12px] border border-soft bg-background focus-within:border-[var(--primary)] transition-colors">
          {activeCommand && (
            <div className="px-2.5 pt-2.5">
              <div className="inline-flex items-center gap-2 rounded-lg border border-[var(--primary)]/25 bg-[var(--info-soft)] px-2 py-1 text-xs text-anchor">
                <span className="font-mono font-semibold text-[var(--primary)]">
                  {activeCommand.cmd}
                </span>
                <span>{activeCommand.label}</span>
                <StatusBadge tone={activeCommand.status === "available" ? "success" : "warning"}>
                  {activeCommand.status === "available" ? "Ready" : "SOON"}
                </StatusBadge>
              </div>
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder="Ask Cosmos anything… (type / for commands)"
            className="block w-full resize-none bg-transparent text-sm text-anchor placeholder:text-muted-foreground outline-none px-3 pt-3 pb-1 max-h-40"
          />

          <div className="flex items-center gap-1 px-1.5 pb-1.5">
            {/* Slash hint */}
            <button
              onClick={() => {
                setPrompt("/");
                requestAnimationFrame(() => textareaRef.current?.focus());
              }}
              className="hidden sm:inline-flex items-center gap-1 h-8 px-2 rounded-md text-[12px] text-muted-foreground hover:bg-muted hover:text-anchor"
              title="Show commands"
            >
              <span className="font-mono text-[11px]">/</span>
              Commands
            </button>

            <div className="ml-auto flex items-center gap-1">
              <Button size="sm" onClick={() => onSend()} disabled={!prompt.trim()}>
                <Send className="h-3.5 w-3.5" /> Ask
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 px-1 flex items-center gap-2 text-[11px] text-muted-foreground">
        <Sparkles className="h-3 w-3 text-[var(--ai)]" />
        Cosmos · Model GPT 5.2
        <span className="ml-auto hidden sm:inline">
          <Kbd>↵</Kbd> send · <Kbd>⇧↵</Kbd> newline · <Kbd>/</Kbd> commands
        </span>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 mx-0.5 rounded border border-soft bg-surface text-[10px] font-medium text-anchor align-middle">
      {children}
    </kbd>
  );
}
